// @ts-ignore: Logger module doesn't have TypeScript declarations
import logger from "@viz-ui/services/logger/logger";
import {
  ChartConfig,
  RawChartData,
  ProcessedChartData,
  ProcessedDataPoint,
  ServiceDependencies,
  ChartDataError,
  IChartBackendService,
  ISorter
} from "./barChartBackendService.types";

/**
 * Framework-agnostic Bar Chart Backend Service
 * 
 * This service handles the data processing for bar charts, including:
 * - Fetching data from the backend API
 * - Processing and transforming raw data into chart-ready format
 * - Handling different data types (dates, numbers, strings)
 * - Sorting and grouping data for visualization
 */
export class BarChartBackendService {
  private readonly chartBackendService: IChartBackendService;
  private readonly sorter: ISorter;

  constructor(dependencies: ServiceDependencies) {
    this.chartBackendService = dependencies.chartBackendService;
    this.sorter = dependencies.sorter;
  }

  /**
   * Fetches raw chart data from the backend API
   * @param tableId - The table identifier
   * @param filter - Filter configuration
   * @param config - Chart configuration
   * @param isBiView - Whether this is for bi-directional view
   * @returns Promise resolving to raw chart data
   */
  async fetchJson(
    tableId: string,
    filter: any,
    config: ChartConfig,
    isBiView: boolean = false
  ): Promise<any> {
    try {
      return await this.chartBackendService.fetchJson(tableId, filter, config, isBiView);
    } catch (error) {
      logger.log(`Error fetching chart data for table ${tableId}:`, error);
      throw new ChartDataError(
        `Failed to fetch chart data for table ${tableId}`,
        { tableId, config, originalError: error }
      );
    }
  }

  /**
   * Processes raw chart data into visualization-ready format
   * @param resultData - Raw data from the backend
   * @param config - Chart configuration
   * @returns Processed data ready for chart rendering
   */
  getRepresentation(resultData: RawChartData, config: ChartConfig): ProcessedChartData {
    logger.log('bar chart data processing - Start');
    
    try {
      let data: ProcessedChartData = [];

      // Determine if we have multiple series
      const multipleSeries = config.chartSeries?.fieldName;

      // Extract data types from the result configuration
      const seriesType = multipleSeries 
        ? (() => {
            if (!resultData.config.series?.data_type) {
              throw new ChartDataError(
                "Error: Multiple series chart configuration mismatch. " +
                "Chart configuration indicates multiple series (chartSeries.fieldName = '" + 
                config.chartSeries?.fieldName + "'), but the data configuration is missing " +
                "series information (resultData.config.series.data_type is undefined). " +
                "Ensure the backend returns proper series configuration for multi-series charts.",
                { 
                  expectedField: config.chartSeries?.fieldName,
                  configProvided: config, 
                  dataConfigProvided: resultData.config 
                }
              );
            }
            return resultData.config.series.data_type;
          })()
        : resultData.config.key.data_type;
      const xAxisType = resultData.config.key.data_type;
      const valueType = resultData.config.values.data_type;

      // Get appropriate parsers for each data type
      const seriesParser = this.chartBackendService.getParserForType(seriesType);
      const xAxisParser = this.chartBackendService.getParserForType(xAxisType);
      const valueParser = this.chartBackendService.getParserForType(valueType);

      // Track unique x-axis keys to ensure consistent data structure
      const uniqueXAxisKeyStringToKey = new Map<string, any>();
      
      logger.log('bar chart data - d3 nesting - Start');
      
      // Nest and group the data using the chart backend service's nesting functionality
      data = this.chartBackendService.nest()
        .key((d: any) => {
          return multipleSeries ? seriesParser(d.series) : "";
        })
        .key((d: any) => {
          const xAxisKey = xAxisParser(d.key);
          if ((xAxisType !== "date" && xAxisType !== "datetime") || xAxisKey) {
            uniqueXAxisKeyStringToKey.set(this.getKeyString(xAxisKey), xAxisKey);
          }
          return xAxisKey;
        })
        .excludeNullKeys(xAxisType === "date" || xAxisType === "datetime")
        .rollup((array: any[]) => {
          if (array.length > 1) {
            throw new ChartDataError(
              "Error: Found multiple data points for single category combination. " +
              "Each combination of series and x-axis values should have exactly one data point, " +
              "but found " + array.length + " data points for the same category.",
              { duplicateDataPoints: array.length, categoryData: array }
            );
          }
          return valueParser(array[0].values[0]);
        })
        .entries(resultData.values);
      
      logger.log('bar chart data - d3 nesting - End');

      logger.log('bar chart data - series iteration - Start');
      
      // Create a Set of unique xAxisKeyStrings for faster lookup
      const uniqueXAxisKeyStrings = new Set(uniqueXAxisKeyStringToKey.keys());

      // Fill in missing data points and sort each series
      data.forEach(series => {
        const valuesMap = new Map(
          series.values.map((value: ProcessedDataPoint) => [
            this.getKeyString(value.key), 
            value
          ])
        );

        // Ensure all series have the same x-axis points (fill with null where missing)
        uniqueXAxisKeyStrings.forEach(xAxisKeyString => {
          if (!valuesMap.has(xAxisKeyString)) {
            const xAxisKey = uniqueXAxisKeyStringToKey.get(xAxisKeyString);
            series.values.push({
              key: xAxisKey,
              values: valueParser(null),
            });
          }
        });

        // Sort x-axis values
        series.values = this.sorter.sort(series.values, {
          dataType: xAxisType,
          valueParser: (item: ProcessedDataPoint) => item.key,
        });
      });
      
      logger.log('bar chart data - series iteration - End');

      logger.log('bar chart data - series sorting - Start');
      
      // Sort the series themselves
      data = this.sorter.sort(data, {
        dataType: seriesType,
        valueParser: (item: any) => item.key,
      });
      
      logger.log('bar chart data - series sorting - End');
      logger.log('bar chart data processing - End');

      return data;
      
    } catch (error) {
      logger.log('Error processing bar chart data:', error);
      
      if (error instanceof ChartDataError) {
        throw error;
      }
      
      throw new ChartDataError(
        'Failed to process bar chart data',
        { config, originalError: error }
      );
    }
  }

  /**
   * Converts a key to its string representation for consistent mapping
   * @param key - The key to convert
   * @returns String representation of the key
   */
  private getKeyString(key: any): string {
    const isKeyDefined = ![undefined, null].includes(key);
    return isKeyDefined ? key.toString() : "";
  }
}

/**
 * Factory function to create a BarChartBackendService with dependencies
 * This is useful for dependency injection patterns
 */
export function createBarChartBackendService(dependencies: ServiceDependencies): BarChartBackendService {
  return new BarChartBackendService(dependencies);
}

/**
 * Angular.js compatibility wrapper factory
 * Creates a service instance compatible with Angular's dependency injection
 */
export function createAngularCompatibleService(
  chartBackendService: IChartBackendService,
  sorter: ISorter
): { fetchJson: Function; getRepresentation: Function } {
  const service = new BarChartBackendService({
    chartBackendService,
    sorter
  });

  return {
    fetchJson: service.fetchJson.bind(service),
    getRepresentation: service.getRepresentation.bind(service)
  };
}
